import { CustomCard } from '@/components/custom-card';
import { ROUTES } from '@/constants/routes';
import {
  LocationsTable,
  LocationsTableColumn,
  PlacesSummary,
  RankedBarChart,
  RegionMap,
  RegionTooltip,
  TaxNumberInput,
} from '@/features/harvest/components';
import { CHART_ROW_COLORS } from '@/features/harvest/constants';
import { useHarvestPlacesRegionStats } from '@/features/harvest/hooks';
import { useYearParam } from '@/hooks/useYearParam';
import { formatNumberOrDash } from '@/utils/numberFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** Klasterlar kesimi (`type=organization`): viloyatlar -> o'sha viloyat
 *  tashkilotlari (qator `id` si — STIR) -> tashkilot details sahifasi. */
export const HarvestClusters = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const year = useYearParam();
  const regionCode = searchParams.get('region') || undefined;
  /** Klasterlar ro'yxatini STIR bo'yicha toraytirish uchun (mahalliy filtr). */
  const taxFilter = searchParams.get('tax_number') || undefined;

  const { data, isLoading } = useHarvestPlacesRegionStats({
    year,
    regionCode,
    type: 'organization',
  });

  /** Xarita tooltipi va sarlavha uchun viloyatlar kesimi kerak. */
  const { data: regionsData } = useHarvestPlacesRegionStats({ year });

  const renderMapTooltip = (id: string) => {
    const region = regionsData?.regions?.find((row) => String(row.id) === id);
    if (!region) return null;
    return <RegionTooltip region={region} />;
  };

  const selectedRegionName = regionsData?.regions?.find(
    (row) => String(row.id) === regionCode,
  )?.name_uz;

  const isRegionsList = !regionCode;

  const updateParams = (changes: Record<string, string | undefined>) => {
    setSearchParams((prev) => {
      Object.entries(changes).forEach(([key, value]) => {
        if (value) prev.set(key, value);
        else prev.delete(key);
      });
      return prev;
    });
  };

  /** Qator `id` si — STIR, shuning uchun filtr shu bo'yicha ishlaydi. */
  const rows = (data?.regions || []).filter(
    (row) => !taxFilter || isRegionsList || String(row.id).includes(taxFilter),
  );

  /** Tashkilot details sahifasiga o'tish — STIR route parametri sifatida. */
  const navigateToCluster = (taxNumber: string) => {
    navigate(
      {
        pathname: `/${ROUTES.HARVEST_CLUSTERS}/${taxNumber}`,
        search: searchParams.toString(),
      },
      {
        state: {
          from: {
            pathname: `/${ROUTES.HARVEST_CLUSTERS}`,
            search: searchParams.toString(),
          },
        },
      },
    );
  };

  /** Viloyatlar bosqichida klaster ko'rsatkichlari ko'rsatiladi. */
  const regionColumns: LocationsTableColumn[] = [
    {
      label: t('clusters_count'),
      value: (row) => formatNumberOrDash(row.clusters_count, ` ${t('ta')}`),
    },
    {
      label: t('count_of_cooperations'),
      value: (row) => formatNumberOrDash(row.cooperations_count, ` ${t('ta')}`),
    },
    {
      label: t('places_count'),
      value: (row) => formatNumberOrDash(row.places_count, ` ${t('ta')}`),
    },
  ];

  return (
    <>
      <CustomCard>
        <div className="flex items-center gap-6">
          <RegionMap
            renderTooltip={renderMapTooltip}
            onBack={
              regionCode
                ? () =>
                    updateParams({ region: undefined, tax_number: undefined })
                : undefined
            }
          />

          <PlacesSummary
            clustersFirst
            className="w-1/2"
            summary={data?.data}
            title={t('cotton_and_places_contracts_info', {
              region: selectedRegionName || t('republic'),
            })}
          />
        </div>
      </CustomCard>

      <div className="grid grid-cols-2 gap-6">
        <RankedBarChart
          title={t('top_cluster_regions')}
          data={(regionsData?.regions || []).map((row) => ({
            name: row.name_uz,
            value: row.clusters_count || 0,
          }))}
          isLoading={isLoading}
          labelInside
          valueSuffix={t('ta')}
        />

        {/* 13 qator: 12 viloyat + Qoraqalpog'iston Respublikasi. */}
        <RankedBarChart
          title={t('top_place_regions')}
          data={(regionsData?.regions || []).map((row) => ({
            name: row.name_uz,
            value: row.places_count || 0,
          }))}
          isLoading={isLoading}
          labelInside
          valueSuffix={t('ta')}
          colors={CHART_ROW_COLORS.blue}
        />
      </div>

      <LocationsTable
        className="w-full"
        title={isRegionsList ? t('clusters_by_regions') : t('clusters')}
        nameLabel={isRegionsList ? t('harvest_region_name') : t('cluster_name')}
        data={rows}
        isLoading={isLoading}
        columns={isRegionsList ? regionColumns : undefined}
        scrollable={!isRegionsList}
        searchable={!isRegionsList}
        /** STIR'ni qo'lda kiritib to'g'ridan-to'g'ri tashkilotga o'tish mumkin. */
        actions={
          !isRegionsList && (
            <TaxNumberInput
              value={taxFilter}
              onApply={(next) =>
                updateParams({ tax_number: next || undefined })
              }
            />
          )
        }
        onBack={
          isRegionsList
            ? undefined
            : () => updateParams({ region: undefined, tax_number: undefined })
        }
        onRowClick={(row) =>
          isRegionsList
            ? updateParams({ region: String(row.id) })
            : navigateToCluster(String(row.id))
        }
      />
    </>
  );
};
